import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { providerService } from '../services/providerService';
import type { CreateProviderRequest, UpdateProviderRequest } from '../types';
import { tokenKeys } from './useTokens';

// Query keys
export const providerKeys = {
  all: ['providers'] as const,
  lists: () => [...providerKeys.all, 'list'] as const,
  list: (filters?: any) => [...providerKeys.lists(), filters] as const,
  details: () => [...providerKeys.all, 'detail'] as const,
  detail: (id: string) => [...providerKeys.details(), id] as const,
};

/**
 * Hook to fetch all providers for the current user
 */
export const useProviders = () => {
  return useQuery({
    queryKey: providerKeys.list(),
    queryFn: () => providerService.getUserProviders(),
    staleTime: 60000, // Consider data fresh for 1 minute
  });
};

/**
 * Hook to fetch a specific provider
 */
export const useProvider = (providerId: string, enabled = true) => {
  return useQuery({
    queryKey: providerKeys.detail(providerId),
    queryFn: () => providerService.getProvider(providerId),
    enabled,
    staleTime: 60000,
  });
};

/**
 * Hook to create a new provider
 */
export const useCreateProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProviderRequest) => providerService.createProvider(data),
    onSuccess: () => {
      // Invalidate provider list to refetch
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      // Providers affect token usability/display
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};

/**
 * Hook to update a provider
 */
export const useUpdateProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ providerId, data }: { providerId: string; data: UpdateProviderRequest }) =>
      providerService.updateProvider(providerId, data),
    onSuccess: (_, variables) => {
      // Invalidate both list and detail queries
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(variables.providerId) });
      // Tokens depend on provider state/template
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};

/**
 * Hook to delete a provider
 */
export const useDeleteProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (providerId: string) => providerService.deleteProvider(providerId),
    onSuccess: () => {
      // Invalidate provider list to refetch
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      // Tokens list should refresh when provider is deleted/disabled
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};
